import React, { useCallback, useState } from 'react';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	MenuListItem,
	MenuList,
	MenuSection,
	MenuSectionHeading,
} from '@atlassian/navigation-system';
import type { AppRecommendations } from '../common/types.tsx';
import { useSelectedRecommendation } from '../controllers/useSelectedRecommendation.tsx';
import { useSelectedRecommendationV2 } from '../controllers/useSelectedRecommendationV2.tsx';
import { useSelectedRecommendationV3 } from '../controllers/useSelectedRecommendationV3.tsx';

import messages from '../messages.tsx';
import {
	recommendationsList,
	recommendationsListV2,
	recommendationsListV3,
} from './recommendationsList.tsx';

type RecommendationProps = {
	appRecommendations: AppRecommendations;
};

/*
	This is the initial version.

	This version does not have async resources in react resource router and does not render on SSR.
*/

const RecommendationsV1 = ({ appRecommendations }: RecommendationProps) => {
	const { formatMessage } = useIntl();
	const { selectedRecommendationState, dismissRecommendation } = useSelectedRecommendation({
		appRecommendations,
	});

	if (selectedRecommendationState?.status !== 'success') return null;

	const selectedRecommendation = recommendationsList.find(
		(recommendation) => recommendation.key === selectedRecommendationState.recommendationKey,
	);

	return (
		selectedRecommendation && (
			<>
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.sectionTitle)}</MenuSectionHeading>
						<MenuList>
							<selectedRecommendation.Component
								recommendationKey={selectedRecommendation.key}
								dismissRecommendation={dismissRecommendation}
							/>
						</MenuList>
					</MenuSection>
				</MenuListItem>
			</>
		)
	);
};

/*
	This version corresponds to feature gate - hot-113350-move-resources-to-rrr
	and it builds on top of v1.

	This version renders does not correctly on SSR but it has async resources in react resource router.

*/
const RecommendationsV2 = ({ appRecommendations }: RecommendationProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const { formatMessage } = useIntl();
	const selectedRecommendationState = useSelectedRecommendationV2({
		appRecommendations,
	});
	const onDismissRecommendation = useCallback(() => {
		setIsDismissed(true);
	}, []);

	if (!selectedRecommendationState?.recommendationKey) return null;

	const selectedRecommendation = recommendationsListV2.find(
		(recommendation) => recommendation.key === selectedRecommendationState.recommendationKey,
	);

	return (
		selectedRecommendation &&
		!isDismissed && (
			<>
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.sectionTitle)}</MenuSectionHeading>
						<MenuList>
							<selectedRecommendation.Component
								recommendationKey={selectedRecommendation.key}
								dismissRecommendation={onDismissRecommendation}
							/>
						</MenuList>
					</MenuSection>
				</MenuListItem>
			</>
		)
	);
};

/*
	This version corresponds to feature gate - hot-113350-growth-recommendation-ssr
	and it builds on top ov v2.

	This version renders correctly on SSR and also consist of having async resources in react resource router.

	It is intended to keep this version at the time of feature gate clean up.
*/
const RecommendationsV3 = ({ appRecommendations }: RecommendationProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const { formatMessage } = useIntl();
	const selectedRecommendationState = useSelectedRecommendationV3({
		appRecommendations,
	});
	const onDismissRecommendation = useCallback(() => {
		setIsDismissed(true);
	}, []);

	if (!selectedRecommendationState?.recommendationKey) return null;

	const selectedRecommendation = recommendationsListV3.find(
		(recommendation) => recommendation.key === selectedRecommendationState.recommendationKey,
	);

	return (
		selectedRecommendation &&
		!isDismissed && (
			<>
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.sectionTitle)}</MenuSectionHeading>
						<MenuList>
							<selectedRecommendation.Component
								recommendationKey={selectedRecommendation.key}
								dismissRecommendation={onDismissRecommendation}
							/>
						</MenuList>
					</MenuSection>
				</MenuListItem>
			</>
		)
	);
};

const RecommendationsRRR = componentWithFG(
	'hot-113350-move-resources-to-rrr',
	RecommendationsV2,
	RecommendationsV1,
);
export const Recommendations = componentWithFG(
	'hot-113350-growth-recommendation-ssr',
	RecommendationsV3,
	RecommendationsRRR,
);
